"use client"

import * as React from "react"
import { animate, utils } from "animejs"
import { cn } from "cn"

import { useDirection } from "./direction"
import { useMotionScope } from "../../hooks/use-motion-scope"
import { blur, distance, duration, ease } from "../../lib/motion"

type RevealTrigger = "mount" | "view"

function whenInView(element: Element, onEnter: () => void) {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return
      observer.disconnect()
      onEnter()
    },
    { rootMargin: "0px 0px -10% 0px" }
  )
  observer.observe(element)
  return () => observer.disconnect()
}

function Reveal({
  trigger = "mount",
  delay = 0,
  offset = distance.md,
  from = "below",
  blurred = true,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  trigger?: RevealTrigger
  delay?: number
  offset?: number
  from?: "below" | "end"
  blurred?: boolean
}) {
  const direction = useDirection()
  const axis = from === "end" ? "translateX" : "translateY"
  const startOffset = from === "end" && direction === "rtl" ? -offset : offset
  const { root } = useMotionScope<HTMLDivElement>(
    ({ root, reduced, scope }) => {
      if (reduced) {
        root.dataset.motionState = "done"
        return
      }
      const play = () =>
        scope.execute(() => {
          utils.set(root, { opacity: 0, [axis]: startOffset, filter: blurred ? `blur(${blur.sm}px)` : "none" })
          root.dataset.motionState = "done"
          animate(root, {
            opacity: 1,
            [axis]: 0,
            filter: blurred ? "blur(0px)" : "none",
            duration: duration.slow,
            delay,
            ease: ease.enter,
          })
        })
      if (trigger === "mount") {
        play()
        return
      }
      return whenInView(root, play)
    },
    [trigger, delay, axis, startOffset, blurred]
  )

  return <div ref={root} data-slot="reveal" data-motion="reveal" className={cn(className)} {...props} />
}

export { Reveal, whenInView, type RevealTrigger }
